import React, {useEffect, useState} from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Input
} from "reactstrap";
import {useHelp} from "../../contexts/HelpContext";
import {helpContent, helpSections, searchHelpTopics} from "../../content/helpContent";
import {HelpCategory} from "../../types/help.types";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import parse from "html-react-parser";
import "../../assets/scss/black-dashboard-react/custom/_help-modal.scss";

const HelpModal: React.FC = () => {
  const {isModalOpen, currentTopicId, closeHelp, navigateToTopic} = useHelp();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<HelpCategory | null>(null);

  // Set active category based on current topic
  useEffect(() => {
    if (currentTopicId && helpContent[currentTopicId]) {
      setActiveCategory(helpContent[currentTopicId].category);
    }
  }, [currentTopicId]);

  // Scroll to topic when it changes
  useEffect(() => {
    if (isModalOpen && currentTopicId) {
      setTimeout(() => {
        const element = document.getElementById(`help-topic-${currentTopicId}`);
        if (element) {
          element.scrollIntoView({behavior: "smooth", block: "start"});
        }
      }, 100);
    }
  }, [isModalOpen, currentTopicId]);

  const handleCategoryClick = (category: HelpCategory) => {
    setActiveCategory(category);
    setSearchQuery("");
    // Scroll to category section
    const element = document.getElementById(`help-category-${category}`);
    if (element) {
      element.scrollIntoView({behavior: "smooth", block: "start"});
    }
  };

  const handleTopicClick = (topicId: string) => {
    navigateToTopic(topicId);
  };

  const searchResults = searchQuery ? searchHelpTopics(searchQuery) : [];

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Modal
          modalClassName="modal-help"
          isOpen={isModalOpen}
          toggle={closeHelp}
          size="xl"
          className="help-modal"
          scrollable
        >
          <ModalHeader>
            <button aria-label="Close" className="close" onClick={closeHelp}>
              <i className="fa fa-times" />
            </button>
          </ModalHeader>
          <ModalBody>
            <Card>
              <CardHeader data-color={color}>
                <CardTitle tag="h3">
                  <i className="fa fa-question-circle mr-2" />
                  TrainerDX Help Documentation
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="help-modal-container">
                  {/* Sidebar Navigation */}
                  <div className="help-modal-sidebar">
                    <div className="help-search-container">
                      <Input
                        type="text"
                        placeholder="Search help topics..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="help-search-input"
                      />
                    </div>

                    {searchQuery && searchResults.length > 0 ? (
                      <div className="help-search-results">
                        <h6>Search Results ({searchResults.length})</h6>
                        <Nav vertical>
                          {searchResults.map((topic) => (
                            <NavItem key={topic.id}>
                              <NavLink
                                href={`#help-topic-${topic.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleTopicClick(topic.id);
                                }}
                                className={currentTopicId === topic.id ? "active" : ""}
                              >
                                {topic.title}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>
                    ) : !searchQuery ? (
                      <>
                        <h6>Categories</h6>
                        <Nav vertical>
                          {Object.values(HelpCategory).map((category) => (
                            <NavItem key={category}>
                              <NavLink
                                href={`#help-category-${category}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCategoryClick(category);
                                }}
                                className={activeCategory === category ? "active" : ""}
                              >
                                {helpSections[category].title}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>

                        {activeCategory && (
                          <div className="help-topics-list mt-3">
                            <h6>Topics in {helpSections[activeCategory].title}</h6>
                            <Nav vertical>
                              {helpSections[activeCategory].topics.map((topicId) => (
                                <NavItem key={topicId}>
                                  <NavLink
                                    href={`#help-topic-${topicId}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleTopicClick(topicId);
                                    }}
                                    className={`help-topic-link ${currentTopicId === topicId ? "active" : ""}`}
                                  >
                                    {helpContent[topicId].title}
                                  </NavLink>
                                </NavItem>
                              ))}
                            </Nav>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="help-search-no-results">
                        <p>No results found for "{searchQuery}"</p>
                      </div>
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="help-modal-content">
                    {searchQuery && searchResults.length > 0 ? (
                      // Show search results
                      <div>
                        <h4>Search Results for "{searchQuery}"</h4>
                        {searchResults.map((topic) => (
                          <div key={topic.id} id={`help-topic-${topic.id}`} className="help-topic-section">
                            <h5>{topic.title}</h5>
                            <div className="help-topic-category-badge">
                              {helpSections[topic.category]?.title || topic.category}
                            </div>
                            <div className="help-topic-content">{parse(topic.content)}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Show all categories and topics
                      Object.values(HelpCategory).map((category) => (
                        <div key={category} id={`help-category-${category}`} className="help-category-section">
                          <h4>{helpSections[category].title}</h4>
                          {helpSections[category].description && (
                            <p className="help-category-description">{helpSections[category].description}</p>
                          )}

                          {helpSections[category].topics.map((topicId) => {
                            const topic = helpContent[topicId];
                            return (
                              <div
                                key={topicId}
                                id={`help-topic-${topicId}`}
                                className={`help-topic-section ${currentTopicId === topicId ? "help-topic-highlighted" : ""}`}
                              >
                                <h5>{topic.title}</h5>
                                <div className="help-topic-content">{parse(topic.content)}</div>
                                {topic.relatedTopics && topic.relatedTopics.length > 0 && (
                                  <div className="help-related-topics">
                                    <strong>Related topics:</strong>{" "}
                                    {topic.relatedTopics.map((relatedId, index) => (
                                      <React.Fragment key={relatedId}>
                                        {index > 0 && ", "}
                                        <a
                                          href={`#help-topic-${relatedId}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleTopicClick(relatedId);
                                          }}
                                        >
                                          {helpContent[relatedId]?.title || relatedId}
                                        </a>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </ModalBody>
        </Modal>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default HelpModal;
